import React, { useState } from 'react';
import { API_URL } from "../../utils/auth";
import CRSTrendArrow from './CRSTrendArrow';
import { formatDate } from '../../utils/dateFormat';

const getStatus = (score) => {
    if (score >= 0 && score <= 3.99) return { color: 'at-risk', label: 'At-Risk' };
    if (score >= 4.0 && score <= 6.99) return { color: 'fair', label: 'Fair' };
    return { color: 'good', label: 'Good' };
};

// Maps the legacy status color names to token text colors.
const STATUS_TEXT = {
    good: 'text-success',
    fair: 'text-warning',
    'at-risk': 'text-error',
    unknown: 'text-gray-400',
};

// Shared 7-column template — kept in sync with the header in ClientList.js.
const ROW_GRID =
    'md:grid md:grid-cols-[0.5fr_1.7fr_1fr_0.6fr_1.8fr_1.8fr_1fr] md:items-center';

// Mobile field label (hidden on desktop where the column header serves instead).
const Label = ({ children }) => (
    <span className="text-xs font-semibold uppercase tracking-tight text-ink/40 md:hidden">
        {children}
    </span>
);

const ClientListDetail = ({ client, onClick, showCheckbox, checked, onCheckboxChange }) => {
    const status = getStatus(client.clientRetentionScore);
    const statusText = STATUS_TEXT[status.color] || STATUS_TEXT.unknown;
    const [showProgramsModal, setShowProgramsModal] = useState(false);
    const programs = client.programs || [];
    const hasPrograms = programs.length > 0;
    const firstProgram = hasPrograms ? programs[0].description : null;
    const morePrograms = hasPrograms && programs.length > 1;

    return (
        <>
            <div
                onClick={onClick}
                className={`${ROW_GRID} mb-3 flex cursor-pointer flex-col gap-2 rounded-lg bg-white p-4 shadow-card transition-colors
                            hover:bg-purple-50/60 md:mb-0 md:gap-0 md:rounded-none md:bg-transparent md:p-0
                            md:border-b md:border-gray-200 md:py-3 md:shadow-none md:hover:bg-purple-50/40`}
            >
                {showCheckbox && (
                    <div className="flex items-center md:justify-center" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={onCheckboxChange}
                            aria-label="Select client"
                            className="h-4 w-4 cursor-pointer accent-purple"
                        />
                    </div>
                )}

                <div className="flex flex-col items-start gap-0.5 font-semibold text-ink">
                    <span>{`${client.firstName || 'Unknown'} ${client.lastName || ''}`.trim()}</span>
                    <span className={`text-sm capitalize ${statusText}`}>&#9679; {status.label}</span>
                </div>

                <div className="flex items-center justify-between font-semibold text-ink/50 md:block">
                    <Label>Company</Label>
                    <span>{client.companyName || 'Unknown'}</span>
                </div>

                <div className="flex items-center justify-between md:block">
                    <Label>CRS</Label>
                    <span className={`flex items-center gap-1 font-semibold ${statusText}`}>
                        {client.formattedCRS !== undefined ? client.formattedCRS : 'N/A'}
                        <CRSTrendArrow crsHistory={client.crsHistory} />
                    </span>
                </div>

                <div className="flex items-center justify-between font-semibold text-ink/50 md:block">
                    <Label>Last Contacted</Label>
                    <span>{client.lastContacted ? formatDate(client.lastContacted) : 'No data available'}</span>
                </div>

                <div className="flex items-center justify-between font-semibold text-ink md:block">
                    <Label>Programs</Label>
                    <span className="flex items-center gap-2">
                        {hasPrograms ? (
                            <>
                                <span className="text-ink/50">{firstProgram}</span>
                                {morePrograms && (
                                    <button
                                        className="font-semibold text-purple underline"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowProgramsModal(true);
                                        }}
                                    >
                                        More
                                    </button>
                                )}
                            </>
                        ) : (
                            <span className="text-ink/50">No programs</span>
                        )}
                    </span>
                </div>

                <div className="flex items-center justify-between font-semibold text-ink/50 md:block">
                    <Label>Onboarding</Label>
                    <span>{client.onboardingProgress !== null ? `${client.onboardingProgress}%` : 'Not Started'}</span>
                </div>
            </div>

            {showProgramsModal && (
                <div
                    className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/30 p-4"
                    onClick={() => setShowProgramsModal(false)}
                >
                    <div
                        className="w-full max-w-md rounded-xl bg-white p-6 text-center shadow-cardlg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="mb-2">
                            Programs for {client.firstName} {client.lastName}
                        </h3>
                        <ul className="mt-4 list-none space-y-2 p-0 text-left">
                            {programs.map((p, idx) => (
                                <li
                                    key={p.id || idx}
                                    className="rounded-md bg-canvas px-4 py-2.5 font-semibold text-ink shadow-card"
                                >
                                    {p.description}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={() => setShowProgramsModal(false)}
                            className="cb-btn-primary mt-5"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientListDetail;
